import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'

export interface Announcement {
  id: string
  title: string
  content: string
  status: string
  created_at: string
  updated_at: string
}

export const useAnnouncements = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()
  const { user } = useAuth()

  const fetchAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })

      if (error) {
        // Handle 401 errors gracefully for SEO crawlers
        if (error.code === 'PGRST301' || error.message?.includes('401')) {
          setAnnouncements([])
          return
        }
        throw error
      }
      setAnnouncements(data || [])
    } catch (error) {
      // Only log and show toast for actual errors, not auth failures
      if (!error?.message?.includes('401')) {
        console.error('Error fetching announcements:', error)
        toast({
          title: 'Error',
          description: 'Failed to fetch announcements',
          variant: 'destructive',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const createAnnouncement = async (
    announcement: Omit<Announcement, 'id' | 'created_at' | 'updated_at'>,
  ) => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .insert([announcement])
        .select()
        .single()

      if (error) throw error

      await fetchAnnouncements()
      toast({
        title: 'Success',
        description: 'Announcement created successfully',
      })
      return data
    } catch (error) {
      console.error('Error creating announcement:', error)
      toast({
        title: 'Error',
        description: 'Failed to create announcement',
        variant: 'destructive',
      })
      throw error
    }
  }

  const updateAnnouncement = async (id: string, updates: Partial<Announcement>) => {
    try {
      const { error } = await supabase.from('announcements').update(updates).eq('id', id)

      if (error) throw error

      await fetchAnnouncements()
      toast({
        title: 'Success',
        description: 'Announcement updated successfully',
      })
    } catch (error) {
      console.error('Error updating announcement:', error)
      toast({
        title: 'Error',
        description: 'Failed to update announcement',
        variant: 'destructive',
      })
      throw error
    }
  }

  const deleteAnnouncement = async (id: string) => {
    try {
      const { error } = await supabase.from('announcements').delete().eq('id', id)

      if (error) throw error

      await fetchAnnouncements()
      toast({
        title: 'Success',
        description: 'Announcement deleted successfully',
      })
    } catch (error) {
      console.error('Error deleting announcement:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete announcement',
        variant: 'destructive',
      })
      throw error
    }
  }

  // Note: view_count and click_count columns don't exist in current schema
  const incrementViewCount = async (id: string) => {
    // No-op for now since column doesn't exist
    console.log('View count tracking not available')
  }

  const incrementClickCount = async (id: string) => {
    // No-op for now since column doesn't exist
    console.log('Click count tracking not available')
  }

  useEffect(() => {
    // Significantly defer announcements to break critical request chain
    const timer = setTimeout(() => {
      fetchAnnouncements()
    }, 1500)
    
    return () => clearTimeout(timer)
  }, [])

  return {
    announcements,
    loading,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    incrementViewCount,
    incrementClickCount,
    refreshAnnouncements: fetchAnnouncements,
  }
}
